import React, { useState, useRef, useEffect } from "react";
import { InformationCircleIcon } from "@heroicons/react/24/outline";

const priorityConfig = {
  critical: {
    color: "#ff4757",
    bg: "rgba(255,71,87,0.12)",
    glow: "rgba(255,71,87,0.3)",
    label: "CRITICAL",
  },
  high: {
    color: "#ffa502",
    bg: "rgba(255,165,2,0.12)",
    glow: "rgba(255,165,2,0.25)",
    label: "HIGH",
  },
  medium: {
    color: "#0099ff",
    bg: "rgba(0,153,255,0.12)",
    glow: "rgba(0,153,255,0.2)",
    label: "MEDIUM",
  },
  low: {
    color: "#00d4aa",
    bg: "rgba(0,212,170,0.12)",
    glow: "rgba(0,212,170,0.2)",
    label: "LOW",
  },
};

export const stateConfig = {
  open: { color: "#00d4aa", label: "Open" },
  in_review: { color: "#0099ff", label: "In Review" },
  awaiting_changes: { color: "#ffa502", label: "Changes Requested" },
  approved: { color: "#00d4aa", label: "Approved" },
  merged: { color: "#a855f7", label: "Merged" },
  closed: { color: "#8b95ab", label: "Closed" },
};

const tagColors = {
  auth: "#ff4757",
  security: "#ff4757",
  backend: "#0099ff",
  frontend: "#a855f7",
  database: "#ffa502",
  docs: "#00d4aa",
  config: "#8b95ab",
  tests: "#00d4aa",
  dependencies: "#8b95ab",
};

// Parses routing-engine.mjs's assignment_reason string ("file expertise
// score: N, queue depth: M" or just "queue depth: M") into a sentence a
// reviewer can actually use. Falls back to the raw string on an unrecognized
// shape rather than hiding it — same non-fatal-degradation philosophy as the
// backend's own error handling.
function formatAssignmentReason(reason) {
  if (!reason) return null;
  const expertiseMatch = reason.match(/file expertise score: (\d+)/);
  const queueMatch = reason.match(/queue depth: (\d+)/);
  const expertise = expertiseMatch ? parseInt(expertiseMatch[1], 10) : null;
  const queueDepth = queueMatch ? parseInt(queueMatch[1], 10) : null;

  if (expertise) {
    const pending = queueDepth !== null ? queueDepth : 0;
    return `Routed for file history — has touched these files ${expertise} time${expertise === 1 ? "" : "s"} before, with ${pending} review${pending === 1 ? "" : "s"} already pending.`;
  }
  if (queueDepth !== null) {
    return `Routed by workload — ${queueDepth} review${queueDepth === 1 ? "" : "s"} already pending, the lightest queue on the team.`;
  }
  return reason;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const hrs = Math.floor(diff / 3600000);
  if (hrs < 1) return "just now";
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function PrCard({ pr }) {
  const [hovered, setHovered] = useState(false);
  const [reasonOpen, setReasonOpen] = useState(false);
  const reasonRef = useRef(null);
  const reason = formatAssignmentReason(pr.assignment_reason);

  // Dismiss on outside click/tap — a popover that only closes via its own
  // trigger traps anyone who taps elsewhere expecting it to go away.
  useEffect(() => {
    if (!reasonOpen) return undefined;
    function handlePointerDown(e) {
      if (reasonRef.current && !reasonRef.current.contains(e.target)) {
        setReasonOpen(false);
      }
    }
    function handleKeyDown(e) {
      if (e.key === "Escape") setReasonOpen(false);
    }
    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [reasonOpen]);

  const cfg = priorityConfig[pr.priority] || priorityConfig.medium;
  const stateCfg = stateConfig[pr.state] || stateConfig.open;
  const tags = pr.tags || [];
  const complexityScore = parseFloat(pr.complexity_score) || 0;
  const complexityPct = Math.min((complexityScore / 10) * 100, 100);
  // Strip the owner from GitHub's "owner/repo" full name. Falls back to the
  // whole string if it ever arrives without a slash.
  const repoName = (pr.repository || "").split("/").pop();
  const complexityColor =
    pr.complexity_score >= 7
      ? "#ff4757"
      : pr.complexity_score >= 4
        ? "#ffa502"
        : "#00d4aa";

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="bg-rf-bg-secondary rounded-xl p-5 transition-all duration-300 cursor-pointer"
      style={{
        border: `1px solid ${hovered ? cfg.color + "40" : "rgba(30,40,64,0.4)"}`,
        transform: hovered ? "translateY(-3px)" : "none",
        boxShadow: hovered ? `0 8px 24px ${cfg.glow}` : "none",
      }}
    >
      {/* Header: Priority + State + Time */}
      <div className="flex justify-between items-start mb-2.5">
        <div className="flex gap-2 items-center">
          <span
            className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-md text-[11px] font-semibold font-mono tracking-wider"
            style={{
              color: cfg.color,
              background: cfg.bg,
              border: `1px solid ${cfg.color}22`,
            }}
          >
            {pr.priority === "critical" && (
              <span
                className="w-1.5 h-1.5 rounded-full animate-pulse"
                style={{
                  background: cfg.color,
                  boxShadow: `0 0 6px ${cfg.glow}`,
                }}
              />
            )}
            {cfg.label}
          </span>
          <span
            className="text-[11px] font-medium px-2 py-0.5 rounded"
            style={{ color: stateCfg.color, background: `${stateCfg.color}18` }}
          >
            {stateCfg.label}
          </span>
        </div>
        <span className="text-[11px] text-rf-text-muted font-mono">
          {timeAgo(pr.github_created_at)}
        </span>
      </div>

      {/* Title */}
      <h3 className="text-[15px] font-semibold text-rf-text-primary mb-1.5 leading-snug">
        {pr.title}
      </h3>

      {/* Author · Repo
          `pr.repository` is GitHub's full name (owner/repo), so showing it
          whole next to the author rendered as "markishou / markishou/reviewflow".
          The owner is redundant here — a team maps to exactly one GitHub
          installation, so every repo it tracks shares the same owner — and the
          "/" separator made the pair read like a single path. Show the bare
          repo name, separated by "·" as used elsewhere in the dashboard. */}
      <div className="text-xs text-rf-text-secondary mb-3">
        <span className="font-mono text-rf-accent-blue">
          {pr.author_github_username || pr.author}
        </span>
        <span className="mx-1.5 text-rf-text-muted">·</span>
        <span>{repoName}</span>
      </div>

      {/* Complexity Bar — item 9: a PR on a repo outside the team's current
          plan is skipped by analyzePr/routePr entirely, so there's no real
          score to show. Say so plainly rather than rendering a misleading
          0.0 bar. */}
      {pr.not_analyzed_reason === "outside_plan" ? (
        <div className="mb-3 text-xs text-rf-text-muted italic">
          Not analyzed — outside your plan
        </div>
      ) : (
        <div className="mb-3">
          <div className="text-[10px] text-rf-text-muted mb-1 uppercase tracking-wider">
            Complexity
          </div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-1 bg-rf-bg-tertiary rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${complexityPct}%`,
                  background: complexityColor,
                }}
              />
            </div>
            <span
              className="text-xs font-mono font-semibold min-w-[28px] text-right"
              style={{ color: complexityColor }}
            >
              {complexityScore.toFixed(1)}
            </span>
          </div>
        </div>
      )}

      {/* Nutrition Label */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-2.5 bg-rf-bg-primary/50 rounded-lg border border-rf-border mb-3">
        {[
          {
            label: "Lines",
            value: `+${pr.lines_added || 0} / -${pr.lines_deleted || 0}`,
            warn: (pr.lines_added || 0) + (pr.lines_deleted || 0) > 400,
          },
          {
            label: "Files",
            value: pr.files_changed || 0,
            warn: (pr.files_changed || 0) > 10,
          },
          {
            label: "Est. time",
            value: `${pr.estimated_review_time || "?"}m`,
            warn: (pr.estimated_review_time || 0) > 30,
          },
        ].map((m) => (
          <div key={m.label} className="text-center">
            <div className="text-[10px] text-rf-text-muted mb-0.5 uppercase tracking-wider">
              {m.label}
            </div>
            <div
              className={`text-[13px] font-mono font-semibold ${m.warn ? "text-rf-accent-warning" : "text-rf-text-primary"}`}
            >
              {m.value}
            </div>
          </div>
        ))}
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div className="flex gap-1 flex-wrap mb-3">
          {tags.map((tag) => {
            const c = tagColors[tag] || "#8b95ab";
            return (
              <span
                key={tag}
                className="text-[10px] px-1.5 py-0.5 rounded font-mono"
                style={{
                  color: c,
                  background: `${c}15`,
                  border: `1px solid ${c}25`,
                }}
              >
                {tag}
              </span>
            );
          })}
        </div>
      )}

      {/*
        Unassigned is a real, reachable state, not a transient one: routing
        runs synchronously inside handlePrOpened, so by the time a PR is
        visible here it either has a reviewer or routing found nobody
        eligible. Until this shipped that case rendered NOTHING at all — the
        card just ended, which reads as a loading glitch rather than a fact
        about the PR.

        Deliberately does NOT say why. The card cannot know: it could be a
        one-person team (routing excludes the PR author), every candidate
        over the Free-tier cap, or something else entirely. Diagnosing it
        needs team composition, which lives on the Dashboard — so the
        explanation belongs there and this stays a plain statement of fact.

        Suppressed for a quota-gated PR. handlePrOpened skips routing for a
        repo outside the plan-active set on purpose, so those are not
        stranded — they are excluded — and the card already says "Not
        analyzed — outside your plan" above. Printing "Unassigned" underneath
        that would state a second, competing reason for the same absence.
      */}
      {!pr.assigned_reviewer && pr.not_analyzed_reason !== "outside_plan" && (
        <div className="flex items-center gap-1.5 pt-3 border-t border-rf-border">
          <div className="w-5 h-5 rounded-full bg-rf-bg-tertiary flex items-center justify-center text-[10px] text-rf-text-muted font-semibold shrink-0">
            ?
          </div>
          <span className="text-xs text-rf-text-muted">Unassigned</span>
        </div>
      )}

      {/* Assigned Reviewer */}
      {pr.assigned_reviewer && (
        <div className="relative flex items-center gap-1.5 pt-3 border-t border-rf-border">
          <div className="w-5 h-5 rounded-full bg-rf-accent-blue/15 flex items-center justify-center text-[10px] text-rf-accent-blue font-semibold shrink-0">
            {pr.assigned_reviewer.charAt(0).toUpperCase()}
          </div>
          <span className="text-xs text-rf-text-secondary">
            Assigned to{" "}
            <span className="text-rf-accent-blue font-mono">
              {pr.assigned_reviewer}
            </span>
          </span>

          {/* Only rendered when a real reason exists — no icon, no layout
              change, for PRs assigned before this shipped or with no data. */}
          {reason && (
            <div className="relative ml-auto shrink-0" ref={reasonRef}>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setReasonOpen((v) => !v);
                }}
                aria-label="Why was this reviewer assigned?"
                aria-expanded={reasonOpen}
                className="flex h-7 w-7 -m-1 items-center justify-center rounded-full text-rf-text-muted transition-colors hover:text-rf-accent-blue hover:bg-rf-bg-tertiary"
              >
                <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
              </button>

              {reasonOpen && (
                <div
                  role="tooltip"
                  className="absolute bottom-full right-0 z-10 mb-2 w-56 rounded-lg border border-rf-border bg-rf-bg-tertiary p-3 text-left text-[11px] leading-relaxed text-rf-text-secondary shadow-xl"
                >
                  {reason}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PrCard;
